import React, { useContext, useEffect, useState } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { QueryProvider } from '@thd-nucleus/data-sources';
import './styles/preferenceCenter.scss';
import './styles/Common.scss';
import { Typography } from '@one-thd/sui-atomic-components';
import { LeftNav } from './components/LeftNav/LeftNav';
import { EmailPreferences } from './components/EmailPreferences/EmailPreferences';
import PostalMailPreferences from './components/PostMailPreferences/PostalMailPreferences';
import { ManageAndModify, TooltipContent } from './components/ManageAndModify/ManageAndModify';
import { Search } from './components/Search/Search';
import { EPCContext } from './contexts/epcContext';
import {
  initialEmailTableData,
  initialPhoneTableData,
  initialSmsPhoneTableData, initialSurveyTableData
} from './components/tableData';
import { NewSearchModal } from './components/NewSearchModal/NewSearchModal';
import {
  convertSubsToPrefs,
  getCombinedPhoneMarketingSubscriptions,
  getSubscriptions,
  hasData,
  hasPendingStatus,
  retrieveEmailPreferences, retrieveMarketingPhonePreferences,

} from './common/sharedFunctions';
import { VisitPrivacyCenter } from './components/VisitPrivacyCenter/VisitPrivacyCenter';
import constants from './constants/constants';
import {
  EmailUnsubscribePreferences
} from './components/EmailUnsubscribePreferences/EmailUnsubscribePreferences';
import {
  setupAutoResizer
} from './utils/general';

import { SurveyEmailPreferences } from './components/SurveyEmailPreferences/SurveyEmailPreferences';
import {
  SurveyEmailUnsubscribePreferences
} from './components/SurveyEmailUnsubscribePreferences/SurveyEmailUnsubscribePreferences';
import { BannerContext, BannerProvider } from './contexts/BannerContext';
import { SurveyFeedback } from './components/SurveyFeedback/SurveyFeedback';
import { SmsTelemarketingPreferences } from './components/SmsTelemarketing/SmsTelemarketingPreferences';

export const Preferences = () => {

  const { channel } = useContext(ExperienceContext);
  const [isMobile, setIsMobile] = useState(true);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSearchedInitiated, setIsSearchedInitiated] = useState(false);
  const [phoneTableData, setPhoneTableData] = useState(initialPhoneTableData());
  const [emailTableData, setEmailTableData] = useState(initialEmailTableData());
  const [surveyEmailTableData, setSurveyEmailTableData] = useState(initialSurveyTableData());
  const [togglePendingConfirmationModal, setPendingConfirmationModal] = useState(false);
  const [toggleNewSearch, setToggleNewSearch] = useState(false);
  const [pending, setPending] = useState([]);
  const [smsTelemarketingTableData, setSmsTelemarketingTableData] = useState(initialSmsPhoneTableData());

  useEffect(() => {
    setupAutoResizer(channel, setIsMobile);
  }, []);

  const {
    closeSuccessAlert,
    openErrorAlert,
    closeErrorAlert,
    hideBanners,
    closeOptOutOfAllBanner,
  } = useContext(BannerContext);

  const showErrorBanner = (message) => {
    closeSuccessAlert();
    openErrorAlert(message);
  };

  const handleSmsAndTelemarketingPreferenceRetrieve = (phoneInput) => {
    return (response) => {
      if (hasData(response)) {
        const subs = getCombinedPhoneMarketingSubscriptions(response);
        setSmsTelemarketingTableData(convertSubsToPrefs(initialSmsPhoneTableData(), subs));
        hideBanners();
        setPending([]);
        if (hasPendingStatus(subs)) {
          setPending(subs);
          setPendingConfirmationModal(true);
          closeSuccessAlert();
        }
        closeErrorAlert();
      }
      setPhone(phoneInput);
      setIsSearchedInitiated(true);
    };
  };

  const handleEmailPreferenceRetrieve = (emailInput) => {
    return (response) => {
      if (hasData(response)) {
        const subs = getSubscriptions(response);
        setEmailTableData(convertSubsToPrefs(initialEmailTableData(), subs));
        setSurveyEmailTableData(convertSubsToPrefs(initialSurveyTableData(), subs));
      }
      setEmail(emailInput);
      setIsSearchedInitiated(true);
      closeErrorAlert();
    };
  };

  const handleError = () => {
    setIsSearchedInitiated(false);
    closeSuccessAlert();
    showErrorBanner(constants.GENERIC_ERROR);
  };

  const handleSignUpSearch = (emailInput, phoneInput) => {
    closeSuccessAlert();
    if (emailInput) {
      retrieveEmailPreferences({
        emailAddress: emailInput,
        callback: handleEmailPreferenceRetrieve(emailInput),
        err: handleError
      });
    }
    if (phoneInput) {
      setPhone(phoneInput);
      retrieveMarketingPhonePreferences({
        phoneNumber: phoneInput,
        callback: handleSmsAndTelemarketingPreferenceRetrieve(phoneInput),
        err: handleError
      });
    }
  };

  const handleNewSearch = (emailInput, phoneInput) => {
    setPending([]);
    setPhone('');
    setSmsTelemarketingTableData(initialSmsPhoneTableData());
    setEmail('');
    setEmailTableData(initialEmailTableData());
    setSurveyEmailTableData(initialSurveyTableData());
    setIsSearchedInitiated(false);
    handleSignUpSearch(emailInput, phoneInput);
    closeSuccessAlert();
    setToggleNewSearch(false);
  };

  const handleNewSearchCancel = () => {
    setToggleNewSearch(false);
  };

  return (
    <EPCContext.Provider value={{
      isMobile,
      email,
      setEmail,
      phone,
      setPhone,
      emailTableData,
      setEmailTableData,
      phoneTableData,
      setPhoneTableData,
      setIsSearchedInitiated,
      pending,
      smsTelemarketingTableData,
      setSmsTelemarketingTableData,
      surveyEmailTableData,
      setSurveyEmailTableData,
      handleSignUpSearch,
      handleEmailPreferenceRetrieve,
      handleSmsAndTelemarketingPreferenceRetrieve,
      handleError,
    }}
    >
      <div className="sui-grid sui-gap-4">

        <ManageAndModify
          isSearchedInitiated={isSearchedInitiated}
          setToggleNewSearch={setToggleNewSearch}
          data-testid="manage-and-modify"
        />

        {toggleNewSearch && (
          <NewSearchModal
            open={toggleNewSearch}
            isMobile={isMobile}
            handleSearch={handleNewSearch}
            handleCancel={handleNewSearchCancel}
          />
        )}

        {!isSearchedInitiated ? (
          <>
            <Search handleSearch={handleSignUpSearch} />
            <EmailUnsubscribePreferences />
            <SurveyEmailUnsubscribePreferences />
            <SmsTelemarketingPreferences isSearchedInitiated={isSearchedInitiated} />
            <PostalMailPreferences
              isMobile={isMobile}
              isSearchedInitiated={isSearchedInitiated}
            />
            <VisitPrivacyCenter />
          </>
        ) : (
          <>
            <EmailPreferences />
            <SurveyEmailPreferences />
            <SmsTelemarketingPreferences isSearchedInitiated={isSearchedInitiated} />
            <PostalMailPreferences
              isMobile={isMobile}
              isSearchedInitiated={isSearchedInitiated}
            />
          </>
        )}
        {TooltipContent()}
      </div>

    </EPCContext.Provider>
  );
};

export function MyPreferenceCenter() {
  return (
    <div
      className="sui-m-auto sui-max-w-screen-2xl sui-bg-primary sui-p-4"
      data-testid="my-preference-center"
    >
      <div className="sui-pb-5">
        <QueryProvider cacheKey="customer-data-governance">
          <Breadcrumbs
            staticCrumbs={[
              {
                url: '/privacy/MyPreferenceCenter',
                label: 'My Preference Center',
              }
            ]}
          />
        </QueryProvider>
      </div>
      <Typography variant="h1" weight="display">MY PREFERENCE CENTER</Typography>

      <div className="sui-grid sui-grid-cols-12 sui-pt-8">
        <div className="sui-hidden sm:sui-block sui-col-span-2">
          <LeftNav />
        </div>
        <div className="sui-col-span-12 sm:sui-col-span-10">
          <BannerProvider>
            <Preferences />
            <SurveyFeedback />
          </BannerProvider>
        </div>
        {/* <div className="hidden sm:block sm:sui-col-span-1" /> */}
      </div>
    </div>
  );
}
